import { useEffect } from 'react';

import { LoginFooter, TurnstileWidget } from 'pages/authentication/login';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AuthPublicApi } from 'apis/AuthPublicApi';
import { AuthApi } from 'apis/AuthApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/authentication/login/styles/Login.scss';

export type SetPasswordMode = 'reset' | 'welcome';

type SetPasswordPageProps = {

    /** `reset` = forgotten-password flow (requires Turnstile, returns to /login).
     *  `welcome` = first-time setup (no Turnstile, signs the user in). 
     *  */
    mode: SetPasswordMode;
};

type TokenStatus = 'checking' | 'invalid' | 'valid';

/**
 * Token-landing page for the password-reset (`/reset-password`) and first-time
 * setup (`/welcome`) email flows. Replaces the legacy ResetPassword.aspx
 * and WelcomeNewUser.aspx WebForms pages. The user arrives from an email link
 * with `?q=<userId>&l=<token>`; the token is validated on mount.
 */
export const SetPassword = ({ mode }: SetPasswordPageProps) => {

    const [state, setState] = useSuperState({
        configError: '',
        confirmPassword: '',
        displayName: '',
        error: '',
        loading: false,
        newPassword: '',
        showPassword: false,
        siteKey: '',
        tokenStatus: 'checking' as TokenStatus,
        turnstileNonce: 0,
        turnstileToken: ''
    });
    const { configError, confirmPassword, displayName, error, loading, newPassword, showPassword, siteKey, tokenStatus,
        turnstileNonce, turnstileToken } = state;

    const navigate = useNavigate();
    const { onLoginSuccess } = useApp();
    const [searchParams] = useSearchParams();

    const { formatMessage } = useIntl();

    const userId = searchParams.get('q') || '';
    const token = searchParams.get('l') || '';
    const hasParams = !!(userId && token);

    // Validate the reset/welcome token on mount (and whenever the link changes).
    useEffect(() => {

        if (!hasParams) {

            setState({ tokenStatus: 'invalid' });
            return undefined;

        }

        let cancelled = false;
        setState({ confirmPassword: '', error: '', newPassword: '', tokenStatus: 'checking' });

        AuthPublicApi.ValidateResetToken(userId, token)
            .then(result => {

                if (cancelled) return;
                setState({ displayName: result?.displayName || '', tokenStatus: 'valid' });

            })
            .catch(() => {

                if (!cancelled) setState({ tokenStatus: 'invalid' });

            });

        return () => {

            cancelled = true;

        };

    }, [hasParams, userId, token, mode]);

    // Reset mode always requires a Turnstile token (the server enforces it), so
    // fetch the site key. Welcome mode has no captcha.
    useEffect(() => {

        if (mode !== 'reset') return;

        AuthApi.GetLoginConfig()
            .then(config => setState({
                configError: config.turnstileSiteKey ? '' : (formatMessage({ id: 'the_security_check_is_unavailable_please.8457bec7', defaultMessage: 'The security check is unavailable. Please refresh and try again.' }) || 'The security check is unavailable. Please refresh and try again.'),
                siteKey: config.turnstileSiteKey || ''
            }))
            .catch(() => setState({ configError: formatMessage({ id: 'the_security_check_is_unavailable_please.8457bec7', defaultMessage: 'The security check is unavailable. Please refresh and try again.' }) || 'The security check is unavailable. Please refresh and try again.' }));

    }, [mode]);

    const validate = (): boolean => {

        if (!newPassword || !confirmPassword) {

            setState({ error: formatMessage({ id: 'please_enter_and_confirm_your_new_passwo.41cb5b9d', defaultMessage: 'Please enter and confirm your new password.' }) || 'Please enter and confirm your new password.' });
            return false;

        }
        if (newPassword !== confirmPassword) {

            setState({ error: formatMessage({ id: 'the_passwords_you_entered_do_not_match.15230930', defaultMessage: 'The passwords you entered do not match.' }) || 'The passwords you entered do not match.' });
            return false;

        }
        return true;

    };

    const handleSubmit = async () => {

        if (loading) return;
        if (!validate()) return;
        if (mode === 'reset' && !turnstileToken) {

            setState({ error: formatMessage({ id: 'please_complete_the_security_check_befor.d12aa14d', defaultMessage: 'Please complete the security check before continuing.' }) || 'Please complete the security check before continuing.' });
            return;

        }

        try {

            setState({ error: '', loading: true });

            if (mode === 'reset') {

                await AuthPublicApi.ResetPassword(userId, token, newPassword, turnstileToken);
                navigate('/login?ResetPassword=true');

            } else {

                await AuthPublicApi.WelcomeSetPassword(userId, token, newPassword);

                // Server has signed the user in; hand off to the dashboard
                // without a full page reload.
                await onLoginSuccess();

            }

        } catch (e: any) {

            const fallback = mode === 'reset'
                ? (formatMessage({ id: 'we_could_not_reset_your_password_please.7436974a', defaultMessage: 'We could not reset your password. Please try again.' }) || 'We could not reset your password. Please try again.')
                : (formatMessage({ id: 'we_could_not_set_your_password_please_tr.83ea0c82', defaultMessage: 'We could not set your password. Please try again.' }) || 'We could not set your password. Please try again.');

            setState({
                error: e?.Message || e?.message || fallback,
                loading: false,
                turnstileNonce: mode === 'reset' ? turnstileNonce + 1 : turnstileNonce,
                turnstileToken: mode === 'reset' ? '' : turnstileToken
            });

        }

    };

    const handleKeyDown = (e: React.KeyboardEvent) => {

        if (e.key === 'Enter') {

            e.preventDefault();
            handleSubmit();

        }

    };

    const buildNumber = document.getElementById('root')?.dataset.build || '';

    const heading = mode === 'welcome'
        ? (formatMessage({ id: 'welcome_to_gmetrix.e1b2e5a3', defaultMessage: 'Welcome to GMetrix' }) || 'Welcome to GMetrix')
        : (formatMessage({ id: 'reset_your_password.d3fb2b26', defaultMessage: 'Reset Your Password' }) || 'Reset Your Password');
    const subtitle = mode === 'welcome'
        ? (formatMessage({ id: 'set_a_password_for_your_account_to_get_s.6175aa9f', defaultMessage: 'Set a password for your account to get started.' }) || 'Set a password for your account to get started.')
        : (formatMessage({ id: 'choose_a_new_password_for_your_account.47f83fff', defaultMessage: 'Choose a new password for your account.' }) || 'Choose a new password for your account.');

    const submitDisabled = loading || (mode === 'reset' && !turnstileToken);

    const renderContent = () => {

        if (tokenStatus === 'checking') {

            return (
                <div className="login-card-subtitle text-center mb-4">
                    {<FormattedMessage defaultMessage="Verifying your link…" id="verifying_your_link.e1a0658f" /> || 'Verifying your link\u2026'}
                </div>
            );

        }

        if (tokenStatus === 'invalid') {

            return (
                <>
                    <div className="login-card-title text-center fw-semibold mb-4">
                        {<FormattedMessage defaultMessage="Link expired" id="link_expired.9433b5cf" /> || 'Link expired'}
                    </div>
                    <div className="login-card-subtitle text-center mb-4">
                        {<FormattedMessage defaultMessage="This link is invalid or has expired. Please return to the sign-in page to request a new one." id="this_link_is_invalid_or_has_expired_plea.21220963" /> || 'This link is invalid or has expired. Please return to the sign-in page to request a new one.'}
                    </div>
                    <GMetrixButton className="login-sign-in-btn w-100" onClick={() => navigate('/login')}>
                        {<FormattedMessage defaultMessage="Return to sign in" id="return_to_sign_in.04437718" /> || 'Return to sign in'}
                    </GMetrixButton>
                </>
            );

        }

        return (
            <>
                <div className="login-card-title text-center fw-semibold mb-4">
                    {heading}
                </div>
                <div className="login-card-subtitle text-center mb-4">
                    {mode === 'welcome' && displayName ? `${formatMessage({ id: 'hi.56ebfdeb', defaultMessage: 'Hi' }) || 'Hi'} ${displayName}! ${subtitle}` : subtitle}
                </div>

                <label className="form-label fw-bold">
                    {<FormattedMessage defaultMessage="New Password" id="new_password.bdda5f8f" /> || 'New Password'}
                </label>
                <div className="login-password-wrapper position-relative mb-2">
                    <GMetrixInput
                        autoComplete="new-password"
                        onChange={e => setState({ error: '', newPassword: e.target.value })}
                        onKeyDown={handleKeyDown}
                        placeholder={formatMessage({ id: 'enter_a_new_password.7f55f702', defaultMessage: 'Enter a new password' }) || 'Enter a new password'}
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                    />
                    <button
                        className="login-eye-btn"
                        onClick={() => setState({ showPassword: !showPassword })}
                        type="button"
                    >
                        <i className={`fa-light fa-lg ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                    </button>
                </div>

                <GMetrixInput
                    autoComplete="new-password"
                    label={formatMessage({ id: 'confirm_password.e786fc3f', defaultMessage: 'Confirm Password' }) || 'Confirm Password'}
                    onChange={e => setState({ confirmPassword: e.target.value, error: '' })}
                    onKeyDown={handleKeyDown}
                    placeholder={formatMessage({ id: 're_enter_your_new_password.db4b474e', defaultMessage: 'Re-enter your new password' }) || 'Re-enter your new password'}
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                />

                {mode === 'reset' && (
                    <>
                        <TurnstileWidget
                            className="d-flex justify-content-center mb-3"
                            onToken={tok => setState({ turnstileToken: tok })}
                            resetNonce={turnstileNonce}
                            siteKey={siteKey}
                        />
                        {configError && (
                            <div className="login-card-subtitle text-center mb-4 text-danger">
                                {configError}
                            </div>
                        )}
                    </>
                )}

                <GMetrixButton
                    className="login-sign-in-btn w-100"
                    disabled={submitDisabled}
                    onClick={handleSubmit}
                >
                    {loading ? (<FormattedMessage defaultMessage="Saving…" id="saving.bdfa2829" /> || 'Saving\u2026') : (mode === 'welcome' ? (<FormattedMessage defaultMessage="Get Started" id="get_started.193270d7" /> || 'Get Started') : (<FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" /> || 'Reset Password'))}
                </GMetrixButton>

                {mode !== 'welcome' && (
                    <div className="login-form-links-row d-flex justify-content-center mb-3">
                        <a className="login-form-link" onClick={() => navigate('/login')}>
                            {<FormattedMessage defaultMessage="Back to sign in" id="back_to_sign_in.f5cb09eb" /> || 'Back to sign in'}
                        </a>
                    </div>
                )}
            </>
        );

    };

    return (
        <div className="login-page">
            <div className="login-form-panel d-flex flex-column position-relative bg-transparent">
                {error && (
                    <div className="login-error-banner w-100 mx-auto mb-3 d-flex align-items-start gap-3">
                        <i className="fa-solid fa-circle-exclamation login-error-icon flex-shrink-0" />
                        <span className="login-error-body flex-grow-1">
                            {error}
                        </span>
                    </div>
                )}

                <div className="login-form-container d-flex flex-column align-items-center">
                    <div className="login-card w-100 bg-white">
                        {renderContent()}
                        <div className="login-policy-note w-100 text-center">
                            {<FormattedMessage defaultMessage="Changes have been made to the GMetrix Privacy Policy." id="changes_have_been_made_to_the_gmetrix_pr.c7f15d34" /> || 'Changes have been made to the GMetrix Privacy Policy.'}
                            {' '}
                            <a href="http://gmetrix.com/Home/PrivacyPolicyEducation" rel="noopener noreferrer" target="_blank">
                                {<FormattedMessage defaultMessage="Click here to view." id="click_here_to_view.24e1aed9" /> || 'Click here to view.'}
                            </a>
                        </div>
                    </div>
                </div>

                <LoginFooter buildNumber={buildNumber} />
            </div>
        </div>
    );

};
